import * as React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import apiClient, { getErrorMessage } from '../services/api-client';
import AuthService from '../services/auth';
import wsClient from '../services/ws';

const ADMIN_TOUR_STAGE_KEY = 'mgo_admin_tour_stage_v1';

function sanitizeRoute(route) {
  if (!route || typeof route !== 'string') {
    return '/servicios';
  }
  if (!route.startsWith('/')) {
    return '/servicios';
  }
  if (route.startsWith('//')) {
    return '/servicios';
  }
  return route;
}

export default function AuthBootstrap() {
  const [searchParams] = useSearchParams();
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const incomingToken = searchParams.get('token') || searchParams.get('jwtToken');
      const nextRoute = sanitizeRoute(searchParams.get('next'));
      const shouldStartAdminTour = searchParams.get('tour') === 'admin';

      if (shouldStartAdminTour) {
        window.sessionStorage.setItem(ADMIN_TOUR_STAGE_KEY, 'servicios');
      }

      if (incomingToken) {
        AuthService.setToken(incomingToken);
      }

      if (!incomingToken && !AuthService.isLogged()) {
        if (!cancelled) {
          setRedirect({ trigger: true, route: '/login' });
        }
        return;
      }

      try {
        await apiClient.get('users/configs', {}, { action: 'bootstrap_user_configs' });
        wsClient.disconnect();
        wsClient.connect();
        if (!cancelled) {
          setRedirect({ trigger: true, route: nextRoute });
        }
      } catch (bootstrapError) {
        AuthService.logout();
        if (!cancelled) {
          setError(getErrorMessage(bootstrapError, 'No se pudo preparar la sesión en la app.'));
        }
      }
    }

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  if (redirect.trigger) {
    return <Navigate to={redirect.route} replace={true} />;
  }

  return (
    <Container maxWidth="sm" disableGutters>
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3, bgcolor: 'background.default' }}>
        <Card sx={{ width: '100%', borderRadius: '16px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, textAlign: 'center' }}>
            {!error && <CircularProgress color="secondary" />}
            <Typography sx={{ fontSize: 24, fontWeight: 900, color: 'text.primary' }}>
              Preparando tu acceso
            </Typography>
            {!error && (
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary' }}>
                Estamos iniciando tu sesión en MiGarita.Online.
              </Typography>
            )}
            {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
